import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BfiDataGrid, Column, MasterDetail } from '../../common/SharedComponents';
import { LoanStatusType, LoanType } from '../common/LoanConstants';
import {
    getLoanGridSettingId,
    onBorrowerCellRender,
    onCityCellRender,
    onStateCellRender,
    onActionCellRender,
    onRenderPortfolioGrid,
    onRowPrepared,
    OnOpenLoanDetail,
    OnOpenLoanSummary,
} from '../common/LoanFunctions';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LoanCommitOrApprovalColumn } from '../../common/SharedComponents';
import { LoanApprovedOrNoteDateColumn } from '../common/components/LoanApprovedOrNoteDateColumn';
import { onStructureTypeCellRender } from '../common/functions/onStructureTypeCellRender';
import { onLoanTermCellRender } from '../common/functions/onLoanTermCellRender';
import { onExtensionCellRender } from '../common/functions/onExtensionCellRender';
import { onRepaidDateCellRender } from '../common/functions/onRepaidDateCellRender';

const StandardLoanTable = ({ statusId = LoanStatusType.Approved, loanTypeId = LoanType.Construction, gridStore }) => {
    const searchState = useSelector((state) => state.searchState);
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const location = useLocation();

    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const dataFunction = (criteria) => {
        criteria.loanTypeId = loanTypeId;
        criteria.statusId = statusId;
        return loanApi.getGrid_StandardLoans(criteria);
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.loan);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const shouldShowStructureType = loanTypeId != LoanType.Land;
    const shouldShowPropertyStatus = loanTypeId == LoanType.BuildRent;

    if (!loanTypeId) {
        return null;
    }

    return (
        <BfiDataGrid
            gridType={getLoanGridSettingId(loanTypeId, statusId)}
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            onRowPrepared={onRowPrepared}
            onCellDblClick={(e) =>
                statusId === LoanStatusType.Approved ||
                statusId === LoanStatusType.Outstanding ||
                statusId === LoanStatusType.Repaid
                    ? OnOpenLoanDetail(e, location)
                    : OnOpenLoanSummary(e, location)
            }
        >
            <Column
                dataField='borrowerEntity'
                caption='Entity'
                cellRender={onBorrowerCellRender}
                name='borrowerEntity'
                minWidth={120}
            />
            <Column
                dataField='propertyCity'
                caption='City'
                cellRender={onCityCellRender}
                width={90}
            />
            <Column
                dataField='propertyState'
                caption='State'
                cellRender={onStateCellRender}
                width={70}
            />
            <Column
                dataField='bfiNumber'
                caption='BFI #'
                dataType='string'
                width={120}
            />
            <Column
                dataField='loanNumber'
                caption='Loan #'
                dataType='string'
                width={80}
            />
            {loanTypeId == LoanType.BuildRent && (
                <Column
                    dataField={'propertyStatus'}
                    caption='Type'
                    width={80}
                    alignment='left'
                />
            )}
            {loanTypeId != LoanType.Land && (
                <Column
                    dataField='structureTypeId'
                    caption='Structure Type'
                    dataType='string'
                    cellRender={onStructureTypeCellRender}
                    width={120}
                />
            )}
            {LoanCommitOrApprovalColumn({ statusId })}
            {LoanApprovedOrNoteDateColumn({ statusId })}
            <Column
                dataField='initialTermMonths'
                caption='Term'
                dataType='string'
                width={60}
                alignment={'left'}
                cellRender={onLoanTermCellRender}
            />
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='repaidDate'
                    caption='Repaid Date'
                    dataType='string'
                    width={110}
                    alignment={'left'}
                    cellRender={onRepaidDateCellRender}
                />
            )}
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='totalRepaidAmount'
                    caption='Repaid Amt'
                    dataType='number'
                    format='currency'
                    width={110}
                    alignment={'left'}
                />
            )}
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='extensionNumber'
                    caption='Ext.'
                    dataType='string'
                    width={40}
                    alignment='left'
                    cellRender={onExtensionCellRender}
                />
            )}
            {/* TODO: Future work related to Defaults */}
            {/* {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='defaults'
                    caption='Defaults'
                    dataType='string'
                    width={110}
                />
            )} */}
            <Column
                dataField='pointOfContact'
                caption='POC'
                dataType='string'
                width={120}
            />
            <Column
                dataField='ownerName'
                caption='User'
                dataType='string'
                width={120}
            />
            <Column
                dataField='broker'
                dataType='string'
                width={120}
            />
            <Column
                dataField='lostType'
                caption={'Reason'}
                dataType='string'
                width={200}
                visible={statusId === LoanStatusType.Lost}
            />
            <Column
                alignment='center'
                cellRender={onActionCellRender}
                width={50}
                allowReordering={false}
                allowSorting={false}
                allowResizing={false}
                type='actions'
                fixed={true}
                fixedPosition={'right'}
            />
            <MasterDetail
                enabled={true}
                render={(e) => onRenderPortfolioGrid(e, statusId, shouldShowStructureType, shouldShowPropertyStatus)}
            />
        </BfiDataGrid>
    );
};

export default StandardLoanTable;
